import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useEventListener } from "./event";

export interface Size {
  height: number;
  width: number;
}

export function useSize(element: HTMLElement | null): Size | undefined {
  const [height, setHeight] = useState<number | undefined>();
  const [width, setWidth] = useState<number | undefined>();

  useLayoutEffect(() => {
    setHeight(element?.clientHeight);
    setWidth(element?.clientWidth);
  }, [element?.clientHeight, element?.clientWidth]);

  useEventListener(window, "resize", () => {
    setHeight(element?.clientHeight);
    setWidth(element?.clientWidth);
  });

  return useMemo(
    () =>
      height !== undefined && width !== undefined
        ? { height, width }
        : undefined,
    [height, width],
  );
}

export function useRefSize<E extends HTMLElement>(): [
  React.RefCallback<E>,
  Size | undefined,
] {
  const [element, setElement] = useState<E | null>(null);
  const [height, setHeight] = useState<number | undefined>();
  const [width, setWidth] = useState<number | undefined>();

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      setHeight(entry?.contentRect.height);
      setWidth(entry?.contentRect.width);
    });

    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
        setHeight(undefined);
        setWidth(undefined);
      }
    };
  }, [element]);

  return [
    setElement,
    useMemo(
      () =>
        height !== undefined && width !== undefined
          ? { height, width }
          : undefined,
      [height, width],
    ),
  ];
}
